import moment from "moment";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { InputLabel, Popover } from "@mui/material";
import { Card, CardBody, Col, Row } from "reactstrap";
import { CiFilter } from "react-icons/ci";

import CustomDatePicker from "views/pages/manage/common/commonComponents/CustomDatePicker";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";

import {
  VENDOR_LIST_INITIAL_FILTERS,
  getVendorFilterCount,
  vendorTypeOptions,
  accountStatusOptions,
} from "./helper";

const ListFilters = ({
  filters,
  setFilters,
  filterCount,
  setFilterCount,
  isLoading,
}) => {
  const masterDropdowns = useSelector(
    (state) => state.globalData.masterDropdowns,
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterForm, setFilterForm] = useState(filters);

  const [buOptions, setBuOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);

  useEffect(() => {
    if (masterDropdowns?.businessArea?.length) {
      const options = masterDropdowns.businessArea.map((item) => ({
        label: item.label,
        value: item.businessAreaId,
      }));
      setBuOptions(options);

      const buPlant = masterDropdowns.businessArea.map((item) => ({
        label: item.sapPlantId,
        value: item.sapPlantId,
      }));
      setPlantOptions(buPlant);
    }
  }, [masterDropdowns?.businessArea]);

  useEffect(() => {
    setFilterForm(filters);
  }, [filters]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filterFormHandler = (value, key) => {
    setFilterForm((prev) => ({ ...prev, [key]: value ?? null }));
  };

  const resetFilterForm = () => {
    handleClose();
    setFilterForm(VENDOR_LIST_INITIAL_FILTERS);
    setFilters(VENDOR_LIST_INITIAL_FILTERS);
    setFilterCount(0);
  };

  const handleApplyFilter = () => {
    handleClose();

    const count = getVendorFilterCount(filterForm);
    setFilterCount(count);
    setFilters(filterForm);
  };

  const {
    searchBy,
    searchByValue,
    vendorType,
    businessAreaIds,
    status,
    onboardedFromDate,
    onboardedToDate,
    sapPlantId,
  } = filterForm;

  const open = Boolean(anchorEl);
  const id = open ? "filter-popover" : undefined;

  return (
    <>
      <AButton
        onClick={handleClick}
        aria-describedby={id}
        updatedStyle={
          filterCount > 0
            ? { border: "1px solid #00B0F5", color: "#00B0F5" }
            : {}
        }
        disabled={isLoading}
      >
        <CiFilter />
        Filters {filterCount > 0 && `(${filterCount})`}
      </AButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Card
          className="mb-0 regular-body label-color"
          style={{ width: "330px", borderRadius: "12px" }}
        >
          <CardBody className="d-flex flex-column">
            <div className="text-bold text-lg mb-2">Filters</div>

            <div className="d-flex flex-column gap-md">
              <AAutoComplete
                label="Vendor Type"
                placeholder="Select"
                isMulti={true}
                isShowCount={true}
                items={vendorTypeOptions}
                currentValue={vendorType}
                handleChange={(value) => filterFormHandler(value, "vendorType")}
                inputKey="vendorType"
              />

              <AAutoComplete
                label="BU"
                placeholder="Select"
                isMulti={true}
                isShowCount={true}
                items={buOptions}
                currentValue={businessAreaIds}
                handleChange={(value) =>
                  filterFormHandler(value, "businessAreaIds")
                }
                inputKey="businessAreaIds"
              />

              <AAutoComplete
                label="SAP Plant Id"
                placeholder="Select"
                isMulti={true}
                isShowCount={true}
                items={plantOptions}
                currentValue={sapPlantId}
                handleChange={(value) => filterFormHandler(value, "sapPlantId")}
                inputKey="sapPlantId"
              />

              <AAutoComplete
                label="Account Status"
                placeholder="Select"
                isMulti={true}
                isShowCount={true}
                items={accountStatusOptions}
                currentValue={status}
                handleChange={(value) => filterFormHandler(value, "status")}
                inputKey="status"
              />

              <div>
                <InputLabel style={{ fontSize: "12px", marginBottom: "4px" }}>
                  Onboarding Date
                </InputLabel>
                <Row>
                  <Col sm="6">
                    <CustomDatePicker
                      placeHolder="From"
                      value={
                        onboardedFromDate ? moment(onboardedFromDate) : null
                      }
                      handler={(index, date, keyName) => {
                        filterFormHandler(date, "onboardedFromDate");
                      }}
                      maxDate={moment()}
                    />
                  </Col>
                  <Col sm="6">
                    <CustomDatePicker
                      placeHolder="To"
                      value={onboardedToDate ? moment(onboardedToDate) : null}
                      handler={(index, date, keyName) => {
                        filterFormHandler(date, "onboardedToDate");
                      }}
                      minDate={moment(onboardedFromDate)}
                      maxDate={moment()}
                      disabled={!onboardedFromDate}
                    />
                  </Col>
                </Row>
              </div>

              <div className="mt-3 d-flex flex-row-reverse justify-content-between">
                <AButton
                  variant="primary_filled"
                  className="w-full"
                  onClick={handleApplyFilter}
                  disabled={
                    !vendorType.length &&
                    !businessAreaIds.length &&
                    !status.length &&
                    !sapPlantId.length &&
                    !onboardedFromDate &&
                    !onboardedToDate &&
                    !searchByValue
                  }
                >
                  Apply filter
                </AButton>

                {filterCount > 0 && (
                  <AButton
                    variant="outline"
                    className="button_remove_left_margin"
                    updatedStyle={{
                      border: "1px solid #CC5F5F",
                      color: "#CC5F5F",
                    }}
                    onClick={resetFilterForm}
                  >
                    Remove filter
                  </AButton>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </Popover>
    </>
  );
};

export default ListFilters;
